import React from "react";
import { useTheme } from "../../Hooks";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";

import { styled, Theme, CSSObject } from "@mui/material/styles";
import useStyles from "./FmLeftDrawer.style";

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: "64px",
    background: "#FBFBFD",
});

const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

interface FmLeftDrawerProps {
    children: React.ReactNode;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    isMobile: boolean;
}

const FmLeftDrawer = ({ children, isOpen, setIsOpen, isMobile }: FmLeftDrawerProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const isRtl = !!(theme.direction === "rtl");

    let drawerWidth: number | string = 350;

    if (isMobile) {
        drawerWidth = "100%";
    }

    const openedMixin = (theme: Theme): CSSObject => ({
        width: drawerWidth,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: "hidden",
    });

    const Drawer = styled(MuiDrawer, {
        shouldForwardProp: (prop) => prop !== "open",
    })(({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...(open && {
            ...openedMixin(theme),
            "& .MuiDrawer-paper": openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            "& .MuiDrawer-paper": closedMixin(theme),
        }),
    }));

    if (isMobile) {
        return (
            <div className={classes.mobile_container}>
                {isOpen ? (
                    <Drawer variant='permanent' open={isOpen}>
                        <DrawerHeader>
                            <IconButton onClick={() => setIsOpen(false)}>
                                <CloseIcon sx={{ fontSize: "36px" }} />
                            </IconButton>
                        </DrawerHeader>
                        {children}
                    </Drawer>
                ) : (
                    <></>
                )}
            </div>
        );
    }

    return (
        <div className={classes.desktop_container}>
            <Drawer variant='permanent' open={isOpen}>
                {isOpen ? (
                    <DrawerHeader>
                        <IconButton onClick={() => setIsOpen(false)}>
                            <ChevronLeftIcon />
                        </IconButton>
                    </DrawerHeader>
                ) : (
                    <DrawerHeader>
                        <IconButton onClick={() => setIsOpen(true)}>
                            <ChevronRightIcon />
                        </IconButton>
                    </DrawerHeader>
                )}
                {children}
            </Drawer>
        </div>
    );
};

export default FmLeftDrawer;
